import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accountLedger,
  balanceSheet,
  createAccount,
  deleteAccount,
  generalLedger,
  incomeStatement,
  listAccounts,
  salesReport,
  updateAccount,
  xlsxUrl,
  type AccountDTO,
} from './accounting.api';
import { formatCop } from '../../lib/format';
import { hasPermission, useMe } from '../../hooks/useAuth';
import { readApiError } from '../../lib/api';

type Tab = 'balance' | 'income' | 'sales' | 'ledger' | 'auxiliary' | 'accounts';

export function AccountingPage() {
  const [tab, setTab] = useState<Tab>('balance');
  const [from, setFrom] = useState(() => new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  return (
    <section>
      <h1 style={{ marginTop: 0 }}>Contabilidad</h1>

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {([
          ['balance', 'Balance General'],
          ['income', 'Estado de Resultados'],
          ['sales', 'Reporte de Ventas'],
          ['ledger', 'Libro Mayor'],
          ['auxiliary', 'Auxiliar'],
          ['accounts', 'Plan de Cuentas'],
        ] as const).map(([k, label]) => (
          <button
            key={k}
            className={tab === k ? 'btn btn-primary' : 'btn'}
            onClick={() => setTab(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab !== 'accounts' && (
        <div className="card" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Desde</label>
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Hasta</label>
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      )}

      {tab === 'balance' && <BalanceSheetTab range={{ from, to }} />}
      {tab === 'income' && <IncomeStatementTab range={{ from, to }} />}
      {tab === 'sales' && <SalesTab range={{ from, to }} />}
      {tab === 'ledger' && <GeneralLedgerTab range={{ from, to }} />}
      {tab === 'auxiliary' && <AuxiliaryTab range={{ from, to }} />}
      {tab === 'accounts' && <AccountsTab />}
    </section>
  );
}

function SalesTab({ range }: { range: { from: string; to: string } }) {
  const q = useQuery({
    queryKey: ['accounting', 'sales', range],
    queryFn: () => salesReport(range),
  });
  if (q.isLoading) return <p>Cargando...</p>;
  if (!q.data) return null;
  const d = q.data;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <a className="btn" href={xlsxUrl('sales', range)}>
          Descargar Excel
        </a>
        <span style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>
          {d.invoiceCount} facturas activas en el periodo
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem',
        }}
      >
        <SmallKpi label="Total bruto" value={formatCop(d.totalGross)} accent />
        <SmallKpi label="Subtotal neto" value={formatCop(d.totalNet)} />
        <SmallKpi label="IVA" value={formatCop(d.totalTax)} />
        <SmallKpi label="Facturas" value={String(d.invoiceCount)} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <h3 style={{ margin: 0, padding: '0.6rem 1rem', background: 'var(--color-bg)' }}>Por cajero</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <tbody>
              {d.byCashier.map((c) => (
                <tr key={c.cashierId} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.4rem 1rem' }}>{c.cashierName}</td>
                  <td style={{ padding: '0.4rem 1rem', textAlign: 'right' }}>{c.count}</td>
                  <td style={{ padding: '0.4rem 1rem', textAlign: 'right' }}>{formatCop(c.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <h3 style={{ margin: 0, padding: '0.6rem 1rem', background: 'var(--color-bg)' }}>
            Por método de pago
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <tbody>
              {d.byMethod.map((m) => (
                <tr key={m.method} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.4rem 1rem' }}>{m.method}</td>
                  <td style={{ padding: '0.4rem 1rem', textAlign: 'right' }}>{m.count}</td>
                  <td style={{ padding: '0.4rem 1rem', textAlign: 'right' }}>{formatCop(m.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <h3 style={{ margin: 0, padding: '0.6rem 1rem', background: 'var(--color-bg)' }}>
          Detalle ({d.rows.length} filas)
        </h3>
        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)', position: 'sticky', top: 0 }}>
                <th style={{ padding: '0.5rem' }}>Fecha</th>
                <th style={{ padding: '0.5rem' }}>Factura</th>
                <th style={{ padding: '0.5rem' }}>Cliente</th>
                <th style={{ padding: '0.5rem' }}>Cajero</th>
                <th style={{ padding: '0.5rem' }}>Métodos</th>
                <th style={{ padding: '0.5rem' }}>Estado</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.invoiceId} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.4rem' }}>{new Date(r.issuedAt).toLocaleDateString('es-CO')}</td>
                  <td style={{ padding: '0.4rem' }}><code>{r.number}</code></td>
                  <td style={{ padding: '0.4rem' }}>{r.customerName}</td>
                  <td style={{ padding: '0.4rem' }}>{r.cashierName}</td>
                  <td style={{ padding: '0.4rem' }}>{r.paymentMethods.join(', ')}</td>
                  <td style={{ padding: '0.4rem' }}>
                    <small style={{ color: r.status === 'CANCELLED' ? 'var(--color-danger)' : 'var(--color-muted)' }}>
                      {r.status}
                    </small>
                  </td>
                  <td style={{ padding: '0.4rem', textAlign: 'right' }}>{formatCop(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SmallKpi({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="card" style={{ borderLeft: accent ? '4px solid var(--color-accent)' : undefined }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--color-muted)' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function BalanceSheetTab({ range }: { range: { from: string; to: string } }) {
  const q = useQuery({ queryKey: ['accounting', 'balance', range], queryFn: () => balanceSheet(range) });
  if (q.isLoading) return <p>Cargando...</p>;
  if (!q.data) return null;
  const d = q.data;
  return (
    <div>
      <a className="btn" href={xlsxUrl('balance-sheet', range)}>Descargar Excel</a>
      <Section title="Activos" rows={d.assets} total={d.totals.assets} />
      <Section title="Pasivos" rows={d.liabilities} total={d.totals.liabilities} />
      <Section title="Patrimonio" rows={d.equity} total={d.totals.equity - d.netIncome} />
      <div className="card" style={{ marginTop: '1rem' }}>
        <strong>Resultado del periodo:</strong> {formatCop(d.netIncome)}
      </div>
    </div>
  );
}

function IncomeStatementTab({ range }: { range: { from: string; to: string } }) {
  const q = useQuery({ queryKey: ['accounting', 'income', range], queryFn: () => incomeStatement(range) });
  if (q.isLoading) return <p>Cargando...</p>;
  if (!q.data) return null;
  const d = q.data;
  return (
    <div>
      <a className="btn" href={xlsxUrl('income-statement', range)}>Descargar Excel</a>
      <Section title="Ingresos" rows={d.income} total={d.totals.income} />
      <Section title="Gastos" rows={d.expenses} total={d.totals.expenses} />
      <div className="card" style={{ marginTop: '1rem', borderColor: 'var(--color-accent)' }}>
        <strong>Utilidad neta:</strong> {formatCop(d.totals.netIncome)}
      </div>
    </div>
  );
}

function Section({ title, rows, total }: { title: string; rows: Array<{ code: string; name: string; balance: number }>; total: number }) {
  return (
    <div className="card" style={{ marginTop: '1rem', padding: 0, overflow: 'hidden' }}>
      <h3 style={{ margin: 0, padding: '0.75rem 1rem', background: 'var(--color-bg)' }}>{title}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {rows.map((r) => (
            <tr key={r.code} style={{ borderTop: '1px solid var(--color-border)' }}>
              <td style={{ padding: '0.5rem 1rem', width: 80 }}><code>{r.code}</code></td>
              <td style={{ padding: '0.5rem 1rem' }}>{r.name}</td>
              <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>{formatCop(r.balance)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                Sin movimientos en este rango.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '1px solid var(--color-border)', background: 'var(--color-bg)' }}>
            <td colSpan={2} style={{ padding: '0.6rem 1rem', textAlign: 'right' }}><strong>Total {title}</strong></td>
            <td style={{ padding: '0.6rem 1rem', textAlign: 'right' }}><strong>{formatCop(total)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function GeneralLedgerTab({ range }: { range: { from: string; to: string } }) {
  const q = useQuery({ queryKey: ['accounting', 'ledger', range], queryFn: () => generalLedger(range) });
  if (q.isLoading) return <p>Cargando...</p>;
  if (!q.data) return null;
  return (
    <div>
      <a className="btn" href={xlsxUrl('general-ledger', range)}>Descargar Excel</a>
      <div className="card" style={{ marginTop: '1rem', padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={{ padding: '0.5rem' }}>Fecha</th>
              <th style={{ padding: '0.5rem' }}>Asiento</th>
              <th style={{ padding: '0.5rem' }}>Descripción</th>
              <th style={{ padding: '0.5rem' }}>Cuenta</th>
              <th style={{ padding: '0.5rem', textAlign: 'right' }}>Débito</th>
              <th style={{ padding: '0.5rem', textAlign: 'right' }}>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem' }}>{new Date(r.date).toLocaleDateString('es-CO')}</td>
                <td style={{ padding: '0.5rem' }}><code>{r.entryNumber}</code></td>
                <td style={{ padding: '0.5rem' }}>{r.description}</td>
                <td style={{ padding: '0.5rem' }}>{r.accountCode} {r.accountName}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{r.debit ? formatCop(r.debit) : ''}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{r.credit ? formatCop(r.credit) : ''}</td>
              </tr>
            ))}
            {q.data.length === 0 && (
              <tr><td colSpan={6} style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-muted)' }}>Sin movimientos.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuxiliaryTab({ range }: { range: { from: string; to: string } }) {
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const [accountId, setAccountId] = useState<number | null>(null);
  const ledger = useQuery({
    queryKey: ['accounting', 'aux', accountId, range],
    queryFn: () => accountLedger(accountId!, range),
    enabled: accountId !== null,
  });

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Cuenta</label>
          <select
            className="input"
            value={accountId ?? ''}
            onChange={(e) => setAccountId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— Selecciona una cuenta —</option>
            {accounts.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} — {a.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {ledger.data && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.75rem 1rem', background: 'var(--color-bg)' }}>
            <strong>{ledger.data.code} — {ledger.data.name}</strong> ·{' '}
            Saldo inicial {formatCop(ledger.data.openingBalance)} ·{' '}
            <strong>Saldo final {formatCop(ledger.data.closingBalance)}</strong>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                <th style={{ padding: '0.5rem' }}>Fecha</th>
                <th style={{ padding: '0.5rem' }}>Asiento</th>
                <th style={{ padding: '0.5rem' }}>Descripción</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Débito</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Crédito</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {ledger.data.movements.map((m, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.5rem' }}>{new Date(m.date).toLocaleDateString('es-CO')}</td>
                  <td style={{ padding: '0.5rem' }}><code>{m.entryNumber}</code></td>
                  <td style={{ padding: '0.5rem' }}>{m.description}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{m.debit ? formatCop(m.debit) : ''}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{m.credit ? formatCop(m.credit) : ''}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCop(m.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AccountsTab() {
  const me = useMe();
  const canWrite = hasPermission(me.data, 'accounting.write');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<AccountDTO | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: createAccount,
    onSuccess: () => {
      setShowCreate(false);
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const updateMut = useMutation({
    mutationFn: (vars: { id: number; body: Partial<AccountDTO> }) =>
      updateAccount(vars.id, vars.body),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const deleteMut = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
    onError: (err) => setError(readApiError(err).message),
  });

  if (q.isLoading) return <p>Cargando...</p>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Plan de Cuentas (PUC)</h2>
        {canWrite && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + Nueva cuenta
          </button>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={{ padding: '0.5rem 1rem' }}>Código</th>
              <th style={{ padding: '0.5rem 1rem' }}>Nombre</th>
              <th style={{ padding: '0.5rem 1rem' }}>Tipo</th>
              <th style={{ padding: '0.5rem 1rem' }}>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {q.data?.map((a) => (
              <tr
                key={a.id}
                style={{
                  borderTop: '1px solid var(--color-border)',
                  opacity: a.isActive ? 1 : 0.5,
                }}
              >
                <td style={{ padding: '0.5rem 1rem' }}><code>{a.code}</code></td>
                <td style={{ padding: '0.5rem 1rem' }}>{a.name}</td>
                <td style={{ padding: '0.5rem 1rem' }}>{a.type}</td>
                <td style={{ padding: '0.5rem 1rem' }}>{a.isActive ? 'activo' : 'inactivo'}</td>
                <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                  {canWrite && (
                    <>
                      <button
                        className="btn"
                        style={{ padding: '4px 10px', fontSize: '0.85rem', marginRight: 6 }}
                        onClick={() => setEditing(a)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn"
                        style={{ padding: '4px 10px', fontSize: '0.85rem' }}
                        onClick={() => {
                          if (confirm(`Eliminar/desactivar cuenta ${a.code}?`)) {
                            deleteMut.mutate(a.id);
                          }
                        }}
                      >
                        ×
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && q.data && (
        <AccountFormModal
          parents={q.data}
          onCancel={() => setShowCreate(false)}
          onSubmit={(b) => createMut.mutate(b)}
          submitting={createMut.isPending}
        />
      )}
      {editing && q.data && (
        <AccountFormModal
          initial={editing}
          parents={q.data.filter((p) => p.id !== editing.id)}
          onCancel={() => setEditing(null)}
          onSubmit={(b) => updateMut.mutate({ id: editing.id, body: b })}
          submitting={updateMut.isPending}
        />
      )}
    </div>
  );
}

function AccountFormModal(props: {
  initial?: AccountDTO;
  parents: AccountDTO[];
  onCancel: () => void;
  onSubmit: (body: { code: string; name: string; type: AccountDTO['type']; parentId?: number | null }) => void;
  submitting: boolean;
}) {
  const [code, setCode] = useState(props.initial?.code ?? '');
  const [name, setName] = useState(props.initial?.name ?? '');
  const [type, setType] = useState<AccountDTO['type']>(props.initial?.type ?? 'asset');
  const [parentId, setParentId] = useState<number | null>(props.initial?.parentId ?? null);

  const isEdit = !!props.initial;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
      }}
    >
      <form
        className="card"
        style={{ width: 460 }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit({ code, name, type, parentId });
        }}
      >
        <h3 style={{ marginTop: 0 }}>{isEdit ? 'Editar cuenta' : 'Nueva cuenta'}</h3>
        <div className="field">
          <label>Código</label>
          <input
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isEdit}
            placeholder="1105"
            required
          />
          {isEdit && (
            <small style={{ color: 'var(--color-muted)' }}>El código no se puede editar.</small>
          )}
        </div>
        <div className="field">
          <label>Nombre</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>Tipo</label>
            <select className="input" value={type} onChange={(e) => setType(e.target.value as AccountDTO['type'])}>
              <option value="asset">Activo</option>
              <option value="liability">Pasivo</option>
              <option value="equity">Patrimonio</option>
              <option value="income">Ingreso</option>
              <option value="expense">Gasto</option>
            </select>
          </div>
          <div className="field">
            <label>Cuenta padre</label>
            <select
              className="input"
              value={parentId ?? ''}
              onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">— Raíz —</option>
              {props.parents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting}>
            {props.submitting ? 'Guardando...' : isEdit ? 'Guardar' : 'Crear'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}
